import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Empty, Input, Segmented, Table, Tooltip, Typography,
} from 'antd';
import {
  ClearOutlined, DownloadOutlined, FileZipOutlined, HomeOutlined, ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  createWaveformArchive, downloadWaveformArchive, downloadWaveformFile,
  getWaveformArchive, getWaveformStatus, refreshWaveformIndex, searchWaveforms,
} from '../services/api';
import { formatFileSize } from '../utils/format';
import WaveformFilterBuilder from '../components/WaveformFilterBuilder';
import WaveformDirectoryTree from '../components/WaveformDirectoryTree';
import WaveformSubfolders from '../components/WaveformSubfolders';
import { createFilterGroup, serializeFilters } from '../components/WaveformFilterBuilder.helpers';
import './Waveforms.css';

const { Text, Title } = Typography;
const DEFAULT_PAGE_SIZE = 50;
const SELECTION_COLUMN_WIDTH = 46;
const isPending = (job) => job?.status === 'queued' || job?.status === 'preparing';
const formatBytes = (value) => Number(value) === 0 ? '0 B' : formatFileSize(Number(value));
const formatMinute = (value) => value && dayjs(value).isValid()
  ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';
const errorMessage = (error, fallback) => error.response?.data?.message || error.message || fallback;
const getPayload = (response) => {
  if (!response.data.success) throw new Error(response.data.message || '请求失败');
  return response.data;
};

export default function Waveforms() {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('keyword');
  const [filters, setFilters] = useState(createFilterGroup);
  const [directory, setDirectory] = useState('');
  const [submitted, setSubmitted] = useState({ mode: 'browse', directory: '' });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
  const [index, setIndex] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(() => new Map());
  const [job, setJob] = useState(null);
  const [creatingArchive, setCreatingArchive] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState(() => new Set());
  const mounted = useRef(false);
  const searchSequence = useRef(0);
  const statusSequence = useRef(0);
  const archiveRequest = useRef(false);
  const submittedRef = useRef(submitted);
  const pageSizeRef = useRef(DEFAULT_PAGE_SIZE);
  const indexGeneration = useRef(undefined);
  const hasSearchResult = useRef(false);
  const retryableBrowseFailure = useRef(false);
  const browseRetryGeneration = useRef(null);
  const indexReady = index?.ready;
  const refreshedAt = index?.refreshed_at;

  const acceptIndex = useCallback(data => {
    setIndex(previous => {
      const known = Date.parse(previous?.refreshed_at);
      const incoming = Date.parse(data.refreshed_at);
      if (Number.isFinite(known) && (!Number.isFinite(incoming) || incoming < known)) return previous;
      return data;
    });
  }, []);

  useEffect(() => {
    mounted.current = true;
    let stopped = false;
    let timer;
    const poll = async () => {
      const sequence = ++statusSequence.current;
      let nextDelay = 15000;
      try {
        const data = getPayload(await getWaveformStatus()).data;
        if (stopped) return;
        if (sequence === statusSequence.current) {
          acceptIndex(data);
          setStatusError('');
        }
        if (data.refreshing || !data.ready) nextDelay = 3000;
      } catch (error) {
        if (!stopped && sequence === statusSequence.current) {
          setStatusError(errorMessage(error, '获取索引状态失败'));
        }
      }
      if (!stopped) timer = setTimeout(poll, nextDelay);
    };
    poll();
    return () => {
      stopped = true;
      mounted.current = false;
      searchSequence.current += 1;
      statusSequence.current += 1;
      clearTimeout(timer);
    };
  }, [acceptIndex]);

  const jobId = job?.id;
  const jobPending = isPending(job);
  useEffect(() => {
    if (!jobId || !jobPending) return undefined;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const data = getPayload(await getWaveformArchive(jobId)).data;
        if (stopped) return;
        setJob(data);
        setArchiveError('');
        if (!isPending(data)) return;
      } catch (error) {
        if (stopped) return;
        // Keep the queued/preparing job visible and retry transient status failures.
        setArchiveError(errorMessage(error, '获取 ZIP 任务状态失败'));
      }
      if (!stopped) timer = setTimeout(poll, 2000);
    };
    timer = setTimeout(poll, 1000);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [jobId, jobPending]);

  const runSearch = useCallback(async (criteria, page, pageSize) => {
    const sequence = ++searchSequence.current;
    const currentStatus = statusSequence.current;
    setLoading(true);
    setSearchError('');
    retryableBrowseFailure.current = false;
    try {
      const data = getPayload(await searchWaveforms({ ...criteria, page, pageSize }));
      if (!mounted.current || sequence !== searchSequence.current) return;
      hasSearchResult.current = true;
      setFiles(data.data);
      setPagination({ current: data.page, pageSize: data.pageSize, total: data.total });
      if (data.index && currentStatus === statusSequence.current) acceptIndex(data.index);
    } catch (error) {
      if (!mounted.current || sequence !== searchSequence.current) return;
      const status = error.response?.status;
      retryableBrowseFailure.current = criteria.mode === 'browse' && (!status || status >= 500);
      setSearchError(errorMessage(error, '搜索波形失败'));
    } finally {
      if (mounted.current && sequence === searchSequence.current) setLoading(false);
    }
  }, [acceptIndex]);

  useEffect(() => {
    runSearch({ mode: 'browse', directory: '' }, 1, DEFAULT_PAGE_SIZE);
  }, [runSearch]);

  useEffect(() => {
    if (indexReady === undefined) return;
    const nextGeneration = refreshedAt || '';
    const previousGeneration = indexGeneration.current;
    indexGeneration.current = nextGeneration;
    if (!indexReady) return;
    const recoverInitialBrowse = !hasSearchResult.current && retryableBrowseFailure.current
      && browseRetryGeneration.current !== nextGeneration;
    const newGeneration = previousGeneration !== undefined && previousGeneration !== nextGeneration;
    if (!newGeneration && !recoverInitialBrowse) return;
    // A ready status may arrive before or after the failed initial request; retry once per generation.
    browseRetryGeneration.current = nextGeneration;
    setFiles([]);
    setPagination(previous => ({ ...previous, current: 1, total: 0 }));
    runSearch(submittedRef.current, 1, pageSizeRef.current);
  }, [indexReady, refreshedAt, searchError, runSearch]);

  const submitCriteria = (criteria, pageSize = pagination.pageSize) => {
    submittedRef.current = criteria;
    pageSizeRef.current = pageSize;
    setSubmitted(criteria);
    setSelectedFiles(new Map());
    setFiles([]);
    setPagination({ current: 1, pageSize, total: 0 });
    runSearch(criteria, 1, pageSize);
  };

  const handleDirectory = nextDirectory => {
    if (nextDirectory === directory) return;
    setDirectory(nextDirectory);
    submitCriteria({ ...submittedRef.current, directory: nextDirectory });
  };

  const handleSearch = (event) => {
    event.preventDefault();
    let criteria;
    try {
      if (mode === 'advanced') criteria = { mode: 'advanced', filters: serializeFilters(filters) };
      else {
        if (!query.trim()) throw new Error('请输入搜索内容');
        criteria = { query: query.trim(), mode: 'keyword' };
      }
    } catch (error) {
      setSearchError(error.message);
      return;
    }
    submitCriteria({ ...criteria, directory });
  };

  const handleReset = () => {
    setQuery('');
    setMode('keyword');
    setFilters(createFilterGroup());
    submitCriteria({ mode: 'browse', directory }, DEFAULT_PAGE_SIZE);
  };

  const handleRefresh = async () => {
    const sequence = ++statusSequence.current;
    setRefreshing(true);
    try {
      const data = getPayload(await refreshWaveformIndex()).data;
      if (mounted.current && sequence === statusSequence.current) {
        acceptIndex(data);
        setStatusError('');
      }
    } catch (error) {
      if (mounted.current && sequence === statusSequence.current) {
        setStatusError(errorMessage(error, '刷新索引失败'));
      }
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  };

  const handleCreateArchive = async () => {
    if (archiveRequest.current || jobPending || selectedFiles.size === 0) return;
    archiveRequest.current = true;
    setCreatingArchive(true);
    setArchiveError('');
    try {
      const data = getPayload(await createWaveformArchive([...selectedFiles.keys()])).data;
      if (mounted.current) setJob(data);
    } catch (error) {
      if (mounted.current) setArchiveError(errorMessage(error, '创建 ZIP 任务失败'));
    } finally {
      archiveRequest.current = false;
      if (mounted.current) setCreatingArchive(false);
    }
  };

  const handleDownload = async (record, archive = false) => {
    const key = `${archive ? 'archive' : 'file'}:${record.id}`;
    setDownloading(prev => new Set(prev).add(key));
    setDownloadError('');
    try {
      if (archive) await downloadWaveformArchive(record.id, record.file_name);
      else await downloadWaveformFile(record.id, record.file_name);
    } catch (error) {
      if (mounted.current) setDownloadError(errorMessage(error, '发起下载失败'));
    } finally {
      if (mounted.current) {
        setDownloading(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    }
  };

  const selectedBytes = useMemo(
    () => [...selectedFiles.values()].reduce((sum, file) => sum + Number(file.file_size || 0), 0),
    [selectedFiles]
  );
  const columns = [
    {
      title: '文件名', dataIndex: 'file_name', width: 360,
      render: (name, record) => (
        <Tooltip title={record.relative_path}>
          <span className="waveform-file-name">{name}</span>
        </Tooltip>
      ),
    },
    {
      title: '相对目录', dataIndex: 'directory', width: 280,
      render: directory => <span className="waveform-directory">{directory || '/'}</span>,
    },
    { title: '大小', dataIndex: 'file_size', width: 110, render: formatBytes },
    { title: '修改时间', dataIndex: 'modified_at', width: 172,
      render: value => <span className="waveform-file-time">{formatMinute(value)}</span> },
    {
      title: '下载', key: 'download', width: 64, fixed: 'right', align: 'center',
      render: (_, record) => (
        <Tooltip title="下载原文件">
          <Button
            type="text"
            aria-label={`下载 ${record.file_name}`}
            icon={<DownloadOutlined />}
            loading={downloading.has(`file:${record.id}`)}
            onClick={() => handleDownload(record)}
          />
        </Tooltip>
      ),
    },
  ];
  const visibleColumns = submitted.mode === 'browse' ? columns.filter(column => column.dataIndex !== 'directory') : columns;
  const tableWidth = visibleColumns.reduce((total, column) => total + column.width, SELECTION_COLUMN_WIDTH);
  const expired = job?.expires_at && dayjs(job.expires_at).isBefore(dayjs());
  const jobLabel = { queued: 'ZIP 已排队', preparing: 'ZIP 正在打包', ready: 'ZIP 已就绪', failed: 'ZIP 打包失败' };

  return (
    <div className="page-shell waveform-page">
      <div className="page-heading">
        <Title level={3} className="page-heading-title">波形库</Title>
      </div>

      <div className="waveform-browser">
        <WaveformDirectoryTree directory={directory} onSelect={handleDirectory}
          refreshedAt={refreshedAt} ready={Boolean(indexReady)} />
        <div className="waveform-browser-content">
          <div className="waveform-current-directory" aria-label="当前目录">
            <Tooltip title="返回根目录">
              <Button aria-label="返回根目录" icon={<HomeOutlined />} disabled={!directory}
                onClick={() => handleDirectory('')} />
            </Tooltip>
            <Text>{directory || '全部目录'}</Text>
          </div>

      <section className="waveform-search" aria-label="波形搜索">
        <form className={`waveform-search-form${mode === 'advanced' ? ' waveform-search-advanced' : ''}`} onSubmit={handleSearch}>
          <Segmented
            aria-label="搜索模式"
            value={mode}
            onChange={setMode}
            options={[{ label: '关键词', value: 'keyword' }, { label: '高级查找', value: 'advanced' }]}
          />
          {mode === 'keyword' && <Input
            className="waveform-query"
            aria-label="搜索内容"
            placeholder="11ax BW160 MCS11"
            value={query}
            onChange={event => setQuery(event.target.value)}
            allowClear
          />}
          <div className="waveform-search-actions">
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>搜索</Button>
            <Tooltip title="重置搜索">
              <Button aria-label="重置搜索" icon={<ClearOutlined />} onClick={handleReset} />
            </Tooltip>
          </div>
          {mode === 'advanced' && <WaveformFilterBuilder value={filters} onChange={setFilters} />}
        </form>
        <div className="waveform-index-status" role="status" aria-live="polite">
          <Badge
            status={index?.refreshing ? 'processing' : index?.ready ? 'success' : statusError || index?.last_error ? 'error' : 'default'}
            text={index?.refreshing ? '索引刷新中' : index?.ready ? '索引就绪' : index ? '索引未就绪' : '正在获取索引状态'}
          />
          {index?.ready && <Text type="secondary">{Number(index.file_count).toLocaleString()} 个文件</Text>}
          <Text type="secondary">最近更新：{formatMinute(index?.refreshed_at)}</Text>
          <Button
            className="waveform-refresh"
            icon={<ReloadOutlined />}
            loading={refreshing || index?.refreshing}
            onClick={handleRefresh}
          >刷新索引</Button>
        </div>
      </section>

      {(statusError || index?.last_error) && (
        <Alert type="warning" showIcon title={statusError || index.last_error} />
      )}
      {searchError && <Alert type="error" showIcon title={searchError} />}
      {downloadError && <Alert type="error" showIcon title={downloadError} />}

      <WaveformSubfolders directory={directory} refreshedAt={refreshedAt}
        ready={Boolean(indexReady)} onSelect={handleDirectory} />

      <section className="waveform-results" aria-label="搜索结果">
        <div className="waveform-selection" aria-live="polite">
          <Text>已选择 <strong>{selectedFiles.size}</strong> 个文件 · {formatBytes(selectedBytes)}</Text>
          <Button
            icon={<FileZipOutlined />}
            disabled={selectedFiles.size === 0 || jobPending}
            loading={creatingArchive}
            onClick={handleCreateArchive}
          >打包 ZIP</Button>
        </div>
        {job && (
          <div className={`waveform-archive waveform-archive-${job.status}`} role="status" aria-live="polite">
            <div className="waveform-archive-details">
              <Badge status={jobPending ? 'processing' : job.status === 'ready' ? 'success' : 'error'} text={jobLabel[job.status]} />
              <Text type="secondary">{job.file_count} 个文件 · {formatBytes(job.total_bytes)}</Text>
              {job.file_name && <Text className="waveform-file-name">{job.file_name}</Text>}
              {job.error && <Text type="danger">{job.error}</Text>}
              {job.status === 'ready' && <Text type="secondary">有效期至 {formatMinute(job.expires_at)}</Text>}
            </div>
            {job.status === 'ready' && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                disabled={Boolean(expired)}
                loading={downloading.has(`archive:${job.id}`)}
                onClick={() => handleDownload(job, true)}
              >{expired ? 'ZIP 已过期' : '下载 ZIP'}</Button>
            )}
          </div>
        )}
        {archiveError && <Alert type="error" showIcon title={archiveError} />}
        <Table
          size="small"
          rowKey="id"
          tableLayout="fixed"
          columns={visibleColumns}
          dataSource={files}
          loading={loading}
          scroll={{ x: tableWidth, y: 'min(640px, max(280px, calc(100vh - 420px)))' }}
          rowSelection={{
            selectedRowKeys: [...selectedFiles.keys()],
            preserveSelectedRowKeys: true,
            columnWidth: SELECTION_COLUMN_WIDTH,
            getCheckboxProps: () => ({ disabled: loading }),
            onChange: (keys, rows) => setSelectedFiles(previous => {
              const next = new Map();
              const available = new Map([...previous, ...rows.filter(Boolean).map(row => [row.id, row])]);
              keys.forEach(key => {
                if (available.has(key)) next.set(key, available.get(key));
              });
              return next;
            }),
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: total => `共 ${total} 个文件`,
            onChange: (page, pageSize) => {
              pageSizeRef.current = pageSize;
              runSearch(submitted, page, pageSize);
            },
          }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={submitted.mode === 'browse' ? '当前目录无文件' : '暂无匹配文件'} /> }}
        />
      </section>
        </div>
      </div>
    </div>
  );
}
